import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  FaPlus, FaBox, FaKey, FaSearch, FaEdit, FaTrash, FaEye,
  FaCrown, FaArrowUp, FaCheck, FaSpinner, FaImage, FaComments,
  FaPause, FaPlay, FaRocket
} from 'react-icons/fa';

export default function MyAnnonces() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [userAds, setUserAds] = useState({ products: [], rentals: [], requests: [] });
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [adType, setAdType] = useState('product');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    location: '',
    price: '',
    images: [],
    contact_email: user?.email || '',
  });

  // Plan limits
  const getPlanLimits = () => {
    if (!subscription) return { total: 1, product: 1, rental: 1, request: 1, name: 'Découverte' };
    
    const plan = subscription.plan || 'decouverte';
    switch (plan) {
      case 'pro':
        return { total: 3, product: 1, rental: 1, request: 1, name: 'Pro' };
      case 'fournisseur':
        return { total: 20, product: 20, rental: 20, request: 0, name: 'Fournisseur' };
      case 'sur_mesure':
        return { total: 999, product: 999, rental: 999, request: 999, name: 'Sur Mesure' };
      default:
        return { total: 1, product: 1, rental: 1, request: 1, name: 'Découverte' };
    }
  };

  const planLimits = getPlanLimits();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subscription
      const subRes = await axios.get(`${API}/subscriptions/user/${user.id}`);
      setSubscription(subRes.data);
      
      // Fetch categories
      const catRes = await axios.get(`${API}/categories`);
      setCategories(catRes.data);
      
      // Fetch user's ads
      await fetchUserAds();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAds = async () => {
    try {
      // Fetch products created by user
      const productsRes = await axios.get(`${API}/products?include_status=all`);
      const userProducts = productsRes.data.filter(p => p.created_by_user_id === user.id);
      
      // Separate products and rentals
      const products = userProducts.filter(p => !p.price_rental || p.price_rental === 0);
      const rentals = userProducts.filter(p => p.price_rental && p.price_rental > 0);
      
      // Fetch user's requests
      const requestsRes = await axios.get(`${API}/requests/user/${user.id}`);
      
      setUserAds({
        products,
        rentals,
        requests: requestsRes.data || []
      });
    } catch (error) {
      console.error('Error fetching user ads:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImage(true);
    const uploadedUrls = [];
    
    for (const file of files.slice(0, 5)) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const imageUrl = response.data.url || response.data.image_url;
        if (imageUrl) uploadedUrls.push(imageUrl);
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls].slice(0, 5)
      }));
      toast.success(`${uploadedUrls.length} image(s) ajoutée(s)`);
    }
    
    setUploadingImage(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const openCreateDialog = (type) => {
    setEditMode(false);
    setEditingAd(null);
    setAdType(type);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (ad, type) => {
    setEditMode(true);
    setEditingAd({ ...ad, adType: type });
    setAdType(type);
    setFormData({
      title: ad.name_fr || ad.title_fr || '',
      description: ad.description_fr || '',
      category_id: ad.category_id || '',
      location: ad.location || '',
      price: ad.price_new || ad.price_rental || ad.budget || '',
      images: ad.images || [],
      contact_email: user?.email || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Veuillez entrer un titre');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (editMode && editingAd) {
        // Update existing ad
        if (editingAd.adType === 'product' || editingAd.adType === 'rental') {
          const payload = {
            name_fr: formData.title,
            name_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            category_id: formData.category_id || null,
            location: formData.location,
            price_new: editingAd.adType === 'product' && formData.price ? parseFloat(formData.price) : null,
            price_rental: editingAd.adType === 'rental' && formData.price ? parseFloat(formData.price) : null,
            show_price: true,
            images: formData.images,
          };
          await axios.put(`${API}/products/${editingAd.id}`, payload);
        } else if (editingAd.adType === 'request') {
          const payload = {
            title_fr: formData.title,
            title_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            category_id: formData.category_id || null,
            location: formData.location,
            budget: formData.price ? parseFloat(formData.price) : null,
            images: formData.images,
          };
          await axios.put(`${API}/requests/update/${editingAd.id}?user_id=${user.id}`, payload);
        }
        
        toast.success('Annonce modifiée avec succès !');
      } else {
        // Create new ad
        if (adType === 'product' || adType === 'rental') {
          const payload = {
            name_fr: formData.title,
            name_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            category_id: formData.category_id || null,
            location: formData.location,
            price_new: adType === 'product' && formData.price ? parseFloat(formData.price) : null,
            price_rental: adType === 'rental' && formData.price ? parseFloat(formData.price) : 0,
            show_price: true,
            images: formData.images,
            status: 'pending',
            created_by_user_id: user.id,
            is_individual_seller: true,
            stock_rental: adType === 'rental' ? 1 : 0,
            availability_status: 'available',
          };
          await axios.post(`${API}/products`, payload);
        } else if (adType === 'request') {
          const payload = {
            title_fr: formData.title,
            title_en: formData.title,
            description_fr: formData.description,
            description_en: formData.description,
            request_type: 'service',
            category_id: formData.category_id || null,
            location: formData.location,
            budget: formData.price ? parseFloat(formData.price) : null,
            images: formData.images,
          };
          await axios.post(`${API}/requests/create?user_id=${user.id}`, payload);
        }
        
        // Increment ad count
        await axios.post(`${API}/subscriptions/user/${user.id}/increment-ad?ad_type=${adType}`);
        toast.success('Annonce créée avec succès ! Elle sera visible après validation.');
      }
      
      setDialogOpen(false);
      setEditMode(false);
      setEditingAd(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ad, type) => {
    setDeleting(ad.id);
    try {
      if (type === 'product' || type === 'rental') {
        await axios.delete(`${API}/products/${ad.id}`);
      } else if (type === 'request') {
        await axios.delete(`${API}/requests/delete/${ad.id}?user_id=${user.id}`);
      }
      
      // Decrement ad count
      await axios.post(`${API}/subscriptions/user/${user.id}/decrement-ad?ad_type=${type}`);
      
      toast.success('Annonce supprimée !');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleStatus = async (ad, type) => {
    setToggling(ad.id);
    const currentStatus = ad.status;
    const newStatus = (currentStatus === 'active' || currentStatus === 'approved' || currentStatus === 'available') 
      ? 'inactive' 
      : 'active';
    
    try {
      if (type === 'product' || type === 'rental') {
        await axios.patch(`${API}/products/${ad.id}/status?status=${newStatus}`);
      } else if (type === 'request') {
        await axios.put(`${API}/requests/update/${ad.id}?user_id=${user.id}`, { status: newStatus });
      }
      
      toast.success(newStatus === 'inactive' ? 'Annonce désactivée' : 'Annonce réactivée');
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Erreur lors du changement de statut');
    } finally {
      setToggling(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category_id: '',
      location: '',
      price: '',
      images: [],
      contact_email: user?.email || '',
    });
  };

  // Calculate used slots
  const totalUsed = userAds.products.length + userAds.rentals.length + userAds.requests.length;
  const productUsed = userAds.products.length;
  const rentalUsed = userAds.rentals.length;
  const requestUsed = userAds.requests.length;

  // Check if can create more ads
  const canCreateProduct = productUsed < planLimits.product && totalUsed < planLimits.total;
  const canCreateRental = rentalUsed < planLimits.rental && totalUsed < planLimits.total;
  const canCreateRequest = requestUsed < planLimits.request && totalUsed < planLimits.total;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
      case 'approved':
      case 'available':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">En attente</Badge>;
      case 'inactive':
        return <Badge className="bg-orange-500">Désactivée</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500">Rejetée</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const renderAdCard = (ad, type) => {
    const title = ad.name_fr || ad.title_fr || 'Sans titre';
    const description = ad.description_fr || '';
    const price = type === 'product' ? ad.price_new : type === 'rental' ? ad.price_rental : ad.budget;
    const priceLabel = type === 'rental' ? '€/jour' : '€';
    const icon = type === 'product' ? <FaBox className="text-[#635BFF]" /> : 
                 type === 'rental' ? <FaKey className="text-[#00D4FF]" /> : 
                 <FaSearch className="text-[#0A2540]" />;

    return (
      <Card key={ad.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex gap-4">
            {/* Image */}
            {ad.images?.[0] ? (
              <img 
                src={ad.images[0]} 
                alt={title}
                className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                {icon}
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-semibold text-[#0A2540] truncate">{title}</h3>
                {getStatusBadge(ad.status)}
              </div>
              <p className="text-sm text-gray-600 line-clamp-1 mb-2">{description}</p>
              <div className="flex items-center gap-3 text-sm">
                {price && <span className="font-bold text-[#635BFF]">{price} {priceLabel}</span>}
                {ad.location && <span className="text-gray-500">📍 {ad.location}</span>}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2 mt-4 pt-3 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={() => {
                if (type === 'request') navigate(`/requests/${ad.id}`);
                else navigate(`/product/${ad.id}`);
              }}
            >
              <FaEye className="mr-1" /> Voir
            </Button>
            <Button variant="outline" size="sm" onClick={() => openEditDialog(ad, type)}>
              <FaEdit className="mr-1" /> Modifier
            </Button>
            
            {/* Toggle status */}
            {(ad.status === 'active' || ad.status === 'approved' || ad.status === 'available') ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleToggleStatus(ad, type)}
                disabled={toggling === ad.id}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                {toggling === ad.id ? <FaSpinner className="animate-spin" /> : <FaPause />}
              </Button>
            ) : ad.status === 'inactive' ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleToggleStatus(ad, type)}
                disabled={toggling === ad.id}
                className="text-green-500 border-green-500 hover:bg-green-50"
              >
                {toggling === ad.id ? <FaSpinner className="animate-spin" /> : <FaPlay />}
              </Button>
            ) : null}
            
            {/* Delete */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-red-500 border-red-500 hover:bg-red-50">
                  {deleting === ad.id ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cette annonce ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => handleDelete(ad, type)}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderEmptySlot = (type, canCreate) => {
    const config = {
      product: { icon: <FaBox className="text-3xl text-gray-300" />, label: 'Produit à vendre', color: '#635BFF' },
      rental: { icon: <FaKey className="text-3xl text-gray-300" />, label: 'Matériel à louer', color: '#00D4FF' },
      request: { icon: <FaSearch className="text-3xl text-gray-300" />, label: 'Demande de service', color: '#0A2540' },
    };
    const { icon, label, color } = config[type];

    return (
      <Card className={`border-dashed ${canCreate ? 'hover:border-solid hover:shadow-md cursor-pointer' : 'opacity-50'}`}>
        <CardContent className="p-6 text-center">
          <div className="mb-3">{icon}</div>
          <p className="text-gray-500 mb-3">{label}</p>
          {canCreate ? (
            <Button 
              size="sm" 
              className="text-white"
              style={{ backgroundColor: color }}
              onClick={() => openCreateDialog(type)}
            >
              <FaPlus className="mr-1" /> Créer
            </Button>
          ) : (
            <Badge variant="outline" className="text-gray-400">Slot utilisé</Badge>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">📋 Mes Annonces</h1>
          <p className="text-gray-600">
            Gérez vos annonces selon votre abonnement {planLimits.name}
          </p>
        </div>

        {/* Subscription Status & Quota */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  planLimits.name === 'Pro' ? 'bg-[#635BFF]' : 
                  planLimits.name === 'Fournisseur' ? 'bg-emerald-500' : 'bg-blue-500'
                }`}>
                  {planLimits.name === 'Pro' ? <FaCrown className="text-white text-xl" /> :
                   planLimits.name === 'Fournisseur' ? <FaRocket className="text-white text-xl" /> :
                   <FaCheck className="text-white text-xl" />}
                </div>
                <div>
                  <p className="font-bold text-xl">Pack {planLimits.name}</p>
                  <p className="text-gray-500">{totalUsed}/{planLimits.total} annonce{planLimits.total > 1 ? 's' : ''} utilisée{totalUsed > 1 ? 's' : ''}</p>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="flex-1 max-w-xs hidden md:block">
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#635BFF] to-[#00D4FF] transition-all"
                    style={{ width: `${(totalUsed / planLimits.total) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>Utilisé: {totalUsed}</span>
                  <span>Disponible: {planLimits.total - totalUsed}</span>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-gradient-to-r from-[#00D4FF] to-[#FF8C5A] hover:opacity-90"
              >
                <FaArrowUp className="mr-2" /> Évoluer mon offre
              </Button>
            </div>
            
            {/* Détail par type pour Pro */}
            {planLimits.total > 1 && (
              <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-[#635BFF]">{productUsed}/{planLimits.product}</p>
                  <p className="text-xs text-gray-500">Produits</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#00D4FF]">{rentalUsed}/{planLimits.rental}</p>
                  <p className="text-xs text-gray-500">Locations</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A2540]">{requestUsed}/{planLimits.request}</p>
                  <p className="text-xs text-gray-500">Demandes</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs for different ad types */}
        <Tabs defaultValue="all" className="mb-6">
          <TabsList className="grid grid-cols-4 w-full max-w-md">
            <TabsTrigger value="all">Toutes ({totalUsed})</TabsTrigger>
            <TabsTrigger value="product">Vente ({productUsed})</TabsTrigger>
            <TabsTrigger value="rental">Location ({rentalUsed})</TabsTrigger>
            <TabsTrigger value="request">Demandes ({requestUsed})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {totalUsed === 0 ? (
              <Card className="p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                  <FaPlus className="text-3xl text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-[#0A2540] mb-2">Créez votre première annonce</h3>
                <p className="text-gray-600 mb-6">
                  Vous pouvez déposer jusqu'à {planLimits.total} annonce{planLimits.total > 1 ? 's' : ''} avec votre pack {planLimits.name}.
                </p>
                <div className="flex justify-center gap-3 flex-wrap">
                  {canCreateProduct && (
                    <Button onClick={() => openCreateDialog('product')} className="bg-[#635BFF]">
                      <FaBox className="mr-2" /> Vendre un produit
                    </Button>
                  )}
                  {canCreateRental && (
                    <Button onClick={() => openCreateDialog('rental')} className="bg-[#00D4FF]">
                      <FaKey className="mr-2" /> Louer du matériel
                    </Button>
                  )}
                  {canCreateRequest && (
                    <Button onClick={() => openCreateDialog('request')} className="bg-[#0A2540]">
                      <FaSearch className="mr-2" /> Publier une demande
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {userAds.products.map(ad => renderAdCard(ad, 'product'))}
                {userAds.rentals.map(ad => renderAdCard(ad, 'rental'))}
                {userAds.requests.map(ad => renderAdCard(ad, 'request'))}
                
                {/* Show create buttons if slots available */}
                {totalUsed < planLimits.total && (
                  <Card className="border-dashed">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <span className="text-gray-500">
                          {planLimits.total - totalUsed} emplacement{planLimits.total - totalUsed > 1 ? 's' : ''} disponible{planLimits.total - totalUsed > 1 ? 's' : ''}
                        </span>
                        <div className="flex gap-2 flex-wrap">
                          {canCreateProduct && (
                            <Button size="sm" onClick={() => openCreateDialog('product')} className="bg-[#635BFF]">
                              <FaPlus className="mr-1" /> Produit
                            </Button>
                          )}
                          {canCreateRental && (
                            <Button size="sm" onClick={() => openCreateDialog('rental')} className="bg-[#00D4FF]">
                              <FaPlus className="mr-1" /> Location
                            </Button>
                          )}
                          {canCreateRequest && (
                            <Button size="sm" onClick={() => openCreateDialog('request')} className="bg-[#0A2540]">
                              <FaPlus className="mr-1" /> Demande
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="product" className="mt-6">
            <div className="space-y-4">
              {userAds.products.length > 0 ? (
                userAds.products.map(ad => renderAdCard(ad, 'product'))
              ) : (
                renderEmptySlot('product', canCreateProduct)
              )}
              {canCreateProduct && userAds.products.length > 0 && (
                <Button onClick={() => openCreateDialog('product')} className="w-full bg-[#635BFF]">
                  <FaPlus className="mr-2" /> Ajouter un produit
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="rental" className="mt-6">
            <div className="space-y-4">
              {userAds.rentals.length > 0 ? (
                userAds.rentals.map(ad => renderAdCard(ad, 'rental'))
              ) : (
                renderEmptySlot('rental', canCreateRental)
              )}
              {canCreateRental && userAds.rentals.length > 0 && (
                <Button onClick={() => openCreateDialog('rental')} className="w-full bg-[#00D4FF]">
                  <FaPlus className="mr-2" /> Ajouter une location
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="request" className="mt-6">
            <div className="space-y-4">
              {userAds.requests.length > 0 ? (
                userAds.requests.map(ad => renderAdCard(ad, 'request'))
              ) : (
                renderEmptySlot('request', canCreateRequest)
              )}
              {canCreateRequest && userAds.requests.length > 0 && (
                <Button onClick={() => openCreateDialog('request')} className="w-full bg-[#0A2540]">
                  <FaPlus className="mr-2" /> Ajouter une demande
                </Button>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Upgrade CTA */}
        <Card className="bg-gradient-to-r from-[#0A2540] to-[#006D8F] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <FaCrown className="text-yellow-400" /> Besoin de plus d'annonces ?
                </h3>
                <p className="text-white/80">
                  {planLimits.name === 'Découverte' 
                    ? 'Passez au pack Pro pour 3 annonces ou Fournisseur pour 20 annonces !'
                    : planLimits.name === 'Pro'
                    ? 'Passez au pack Fournisseur pour 20 annonces et une boutique dédiée !'
                    : 'Contactez-nous pour une offre sur mesure !'}
                </p>
              </div>
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-white text-[#0A2540] hover:bg-gray-100"
              >
                Voir les offres
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); resetForm(); setEditMode(false); setEditingAd(null); } else { setDialogOpen(true); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Modifier l\'annonce' : `Nouvelle annonce - ${
                  adType === 'product' ? 'Produit à vendre' : 
                  adType === 'rental' ? 'Matériel à louer' : 
                  'Demande de service'
                }`}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">
                  {adType === 'request' ? 'Titre de votre demande *' : 'Titre de votre annonce *'}
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={
                    adType === 'product' ? 'Ex: Tractopelle JCB 3CX - Bon état' :
                    adType === 'rental' ? 'Ex: Groupe électrogène 50 kVA' :
                    'Ex: Recherche étude de sol pour construction'
                  }
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Décrivez votre annonce en détail..."
                  rows={4}
                />
              </div>
              
              <div>
                <Label>Catégorie</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name_fr || cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location">Localisation</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Ville, Pays"
                  />
                </div>
                <div>
                  <Label htmlFor="price">
                    {adType === 'product' ? 'Prix (€)' : 
                     adType === 'rental' ? 'Prix/jour (€)' : 
                     'Budget (€)'}
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <Label>Photos (max 5)</Label>
                <div className="mt-2">
                  <div className="flex flex-wrap gap-3 mb-3">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20">
                        <img src={img} alt="" className="w-full h-full object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-50">
                        {uploadingImage ? (
                          <FaSpinner className="animate-spin text-gray-400" />
                        ) : (
                          <FaImage className="text-gray-400" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); setEditMode(false); setEditingAd(null); }}>
                  Annuler
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#635BFF]">
                  {submitting ? <FaSpinner className="animate-spin mr-2" /> : <FaCheck className="mr-2" />}
                  {editMode ? 'Enregistrer' : 'Publier l\'annonce'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
